import { useMemoizedHTML } from '../../hooks/useMemoizedHTML';
import { StyleWysiwygContent, StyledWysiwygContainer } from './WysiwygContent.styled';
import { contentHTMLParser } from './contentHTMLParser';
import { StyledContentContainer } from '../../containers/StyledContentContainer.styled';
import { jsx as _jsx } from "react/jsx-runtime";
export const CONTENT_PLACEHOLDER = '<p data-id="paragraph-tag"><span><strong>Super WysiwygContent&nbsp;Element Block</strong> <em>Place&nbsp;content&nbsp;here</em></span></p>';
/**
 * Styling and wrappers split out into their own components.
 *
 * Rich Mahogany handles wysiwyg content differently from all other page blocks, which necessitated
 * breaking up this page block into two components.
 *
 * In Whammy!, only use <WysiwygContent />.
 *
 * In Rich Mahogany:
 * - When the wysiwyg block is not selected, it uses <WysiwygContent />.
 * - When the editor is active, it uses <WysiwygContainer><!-- editor --></WysiwygContainer>.
 */
export const WysiwygContainer = ({
  cssBackgroundColor,
  cssTextAlign,
  parentLayoutInfo,
  children,
  ...rest
}) => {
  /**
   * The cssTextAlign prop aligns both StyledWysiwygContainer and contentHTML.
   *
   * To make things even sillier, this logic only affects the page block
   * when it is displayed in a one-column layout. See WysiwygStyledContentContainer.
   *
   * Per the builder, the (text) "Align" control uses "start", "center", "end", and "justify".
   * The checks below will need to be updated if the builder changes their control.
   */
  let alignXPreset;
  if (cssTextAlign === 'start') {
    alignXPreset = 'left';
  } else if (cssTextAlign === 'end') {
    alignXPreset = 'right';
  } else if (cssTextAlign === 'center' || cssTextAlign === 'justify') {
    alignXPreset = 'center';
  }
  return /*#__PURE__*/_jsx(StyledContentContainer, {
    parentLayoutInfo: parentLayoutInfo,
    children: /*#__PURE__*/_jsx(StyledWysiwygContainer, {
      "data-id": "cp-wysiwyg-content",
      cssBackgroundColor: cssBackgroundColor,
      cssTextAlign: cssTextAlign,
      alignXPreset: alignXPreset,
      parentLayoutInfo: parentLayoutInfo,
      ...rest,
      children: children
    })
  });
};

/**
 * `WysiwygContent` is a minimally styled content block and exists to parse the
 * `contentHTML` string output from Rich Mahogany.
 *
 * ---
 *
 * ! 🚨**[WARNING] This Element block should not be used internally by other blocks!**🚨
 *
 * ---
 *
 * The outer tag in `contentHTML` will get matched and plucked off
 * where it will be set as the outer block element.
 * This is because React requires an outer tag for HTML parsing.
 * It is not possible to `dangerouslySetInnerHTML` on a fragment.
 * Non-global styling is expected to be handled by inline styles in `contentHTML`.
 *
 * ```jsx
 * <WysiwygContent contentHTML={`<p style="...">Lorem ipsum</p>`} />
 * <!-- becomes -->
 * <p style="...">Lorem ipsum</p>
 * ```
 *
 * For standardized typography styles that match the Campaign Studio design system, use the
 * `TYPOGRAPHY_PRESETS` object:
 *
 * ```jsx
 * import { cssObjectToString, TYPOGRAPHY_PRESETS } from '@classy/campaign-page-blocks'
 *
 * <WysiwygContent contentHTML={`<p style="${cssObjectToString(TYPOGRAPHY_PRESETS.themeParagraphLg)}}">Lorem ipsum</p>`} />
 * ```
 */
export const WysiwygContent = ({
  contentHTML = CONTENT_PLACEHOLDER,
  cssBackgroundColor,
  cssTextAlign,
  parentLayoutInfo,
  ...rest
}) => {
  const [tag, innerContentHTML] = contentHTMLParser(contentHTML);
  return /*#__PURE__*/_jsx(WysiwygContainer, {
    cssBackgroundColor: cssBackgroundColor,
    cssTextAlign: cssTextAlign,
    parentLayoutInfo: parentLayoutInfo,
    ...rest,
    children: /*#__PURE__*/_jsx(StyleWysiwygContent, {
      as: tag,
      dangerouslySetInnerHTML: useMemoizedHTML(innerContentHTML)
    })
  });
};